import { Images } from '@cp949/web-image-util';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import type { Project, ProjectBreedKey } from '@ocode/domain';
import { ApiRequestContext } from '@ocodelib/api-common';
import { Center, StackColumn, TopAbsolute } from '@ocodelib/mui';
import { useLoadingVisible } from '@ocodelib/mui/hooks';
import { isNotBlank, limitMaxLength, strlen, trimToUndef } from '@ocodelib/util/string';
import { CustomDialogTitle } from '@repo-foundation/components/CustomDialogTitle';
import type { ProjectKey } from '@repo/project-interface';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CustomDialog } from '../../components/CustomDialog';
import { useApi } from '../../provider/useApi';
import { useLoginUser } from '../../store/useAuthStore';
import { handleError } from '../../util/handle-error';

const MAX_LEN_PROJECT_NM = 30;
const MAX_LEN_MEMO = 250;

export interface ProjectSaveDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (project: Project) => void;
  projectName: string;

  breed: ProjectBreedKey;

  /**
   * 수정 모드에서는 값이 존재
   */
  projectId?: string;

  parentProjectId?: string;

  /**
   * 썸네일 이미지
   * P5, BLOX 프로젝트는 썸네일이 필수
   */
  thumbUrl?: string;

  createDataFile: () => Promise<File>;
}

type InputData = {
  projectName: string;
  memo?: string;
};

type SaveStep = 'none' | 'zipping' | 'uploading' | 'saving' | 'success' | 'error';

export function ProjectSaveDialog(props: ProjectSaveDialogProps) {
  const { projectId, parentProjectId, thumbUrl, breed, open, onClose } = props;
  const api = useApi();
  const [inputData, setInputData] = useState<Partial<InputData>>({
    projectName: props.projectName,
  });
  const [saveStep, setSaveStep] = useState<SaveStep>('none');
  const [loading, setLoading] = useState(false);
  const loadingVisible = useLoadingVisible(loading);
  const { profile } = useLoginUser();
  const createDataFileRef = useRef(props.createDataFile);
  createDataFileRef.current = props.createDataFile;

  const handleClose = () => {
    onClose();
  };

  const updatePart = (part: Partial<InputData>) => {
    setInputData((prev) => ({ ...prev, ...part }));
  };

  // 수정모드에서 프로젝트 정보 로드
  const doLoadProjectInfo = useCallback(
    async (ctx: ApiRequestContext, projectId: string) => {
      try {
        setLoading(true);
        const { project } = await api.project.info({ ctx, projectId });
        setInputData((prev) => ({ ...prev, memo: project.memo }));
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  // 수정모드에서 프로젝트 정보 로드
  useEffect(() => {
    if (!projectId) return;
    const ctx = ApiRequestContext.of();
    doLoadProjectInfo(ctx, projectId);
    return () => {
      ctx.cancel();
    };
  }, [doLoadProjectInfo, projectId]);

  // 프로젝트 저장
  const doSave = useCallback(
    async (params: {
      breed: ProjectBreedKey;
      projectKey: ProjectKey | undefined;
      parentProjectId: string | undefined;
      projectName: string;
      memo?: string;
      thumbUrl?: string;
    }): Promise<Project | null> => {
      const { breed, parentProjectId, projectName, memo, thumbUrl } = params;
      try {
        setSaveStep('zipping');
        const zipFile = await createDataFileRef.current();
        setSaveStep('uploading');
        const projectFileId = (
          await api.project.uploadProjectCodeFile({
            file: zipFile,
            fileName: zipFile.name,
          })
        ).file.fileId;

        let projectThumbFileId: string | undefined = undefined;
        if (thumbUrl) {
          const thumbBlob = await Images.resizeFrom(thumbUrl)
            .centerInside({
              size: 200,
              trim: true,
            })
            .toFile('png', 'thumb.png');

          projectThumbFileId = (
            await api.project.uploadProjectThumb({
              file: thumbBlob,
              fileName: 'thumb.png',
            })
          ).file.fileId;
        }

        setSaveStep('saving');
        if (projectId) {
          // 수정
          const { project } = await api.project.update({
            projectId,
            projectName,
            memo,
            projectFileId,
            projectThumbFileId,
          });
          setSaveStep('success');
          return project;
        } else {
          // 신규 등록
          const { project } = await api.project.create({
            breed,
            projectName,
            memo,
            projectFileId,
            projectThumbFileId,
            parentProjectId,
          });
          setSaveStep('success');
          return project;
        }
      } catch (err) {
        handleError(err);
        setSaveStep('error');
      }
      return null;
    },
    [api],
  );

  const handleClickSaveBtn = () => {
    if (!profile) {
      toast.warn('로그인이 필요합니다');
      return;
    }
    const projectName = trimToUndef(inputData.projectName);
    const memo = trimToUndef(inputData.memo);
    if (!projectName) {
      toast.warn('프로젝트 이름을 입력해주세요');
      return;
    }

    if (projectName.length < 2 || projectName.length > MAX_LEN_PROJECT_NM) {
      toast.warn(`프로젝트 이름을 2~${MAX_LEN_PROJECT_NM}글자로 입력해주세요`);
      return;
    }

    if (memo && memo.length > 0) {
      if (memo.length > MAX_LEN_MEMO) {
        toast.warn(`메모를 ${MAX_LEN_MEMO}글자 이하로 입력해주세요`);
      }
    }

    const accountId = profile.accountId;
    let projectKey: ProjectKey | undefined;
    if (isNotBlank(projectId)) {
      projectKey = {
        projectId,
        accountId,
      };
    }

    doSave({
      breed,
      projectKey,
      parentProjectId,
      projectName,
      memo,
      thumbUrl,
    }).then((savedProject) => {
      if (savedProject) {
        toast.success('저장되었습니다');
        props.onSaved(savedProject);
      }
    });
  };

  const progressVisible =
    loadingVisible || saveStep === 'zipping' || saveStep === 'uploading' || saveStep === 'saving';

  const { projectName = '', memo = '' } = inputData ?? {};

  return (
    <CustomDialog
      className="ProjectSaveDialog-root"
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={handleClose}
    >
      <CustomDialogTitle title="프로젝트 저장" onClose={handleClose} />
      <DialogContent
        dividers
        sx={{
          position: 'relative',
          minWidth: 300,
          px: {
            xs: 1,
            md: 3,
          },
          pt: 1,
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {thumbUrl && (
          <Center
            sx={{
              position: 'relative',
              width: '100%',
              border: '1px solid #eee',
              bgcolor: '#f0f0f0',
              mb: 3,

              '& img': {
                p: 1,
                width: 200,
                height: 200,
                objectFit: 'contain',
              },
            }}
          >
            <img src={thumbUrl} alt="" className="lightbox" />
          </Center>
        )}

        <StackColumn alignItems="flex-start" spacing={4}>
          <TextField
            value={projectName}
            label="프로젝트 이름"
            disabled={saveStep !== 'none' && saveStep !== 'error'}
            autoComplete="off"
            required
            fullWidth
            onChange={(e) => {
              const projectName = limitMaxLength(e.target.value, MAX_LEN_PROJECT_NM);
              updatePart({ projectName });
            }}
            helperText={
              <Box sx={{ textAlign: 'right', display: 'block' }} component="span">
                {strlen(projectName)} / {MAX_LEN_PROJECT_NM}
              </Box>
            }
          />
          <TextField
            value={inputData?.memo ?? ''}
            disabled={saveStep !== 'none' && saveStep !== 'error'}
            label="한줄 메모"
            autoComplete="off"
            fullWidth
            onChange={(e) => {
              const memo = limitMaxLength(e.target.value, MAX_LEN_MEMO);
              updatePart({ memo });
            }}
            helperText={
              <Box sx={{ textAlign: 'right', display: 'block' }} component="span">
                {strlen(memo)} / {MAX_LEN_MEMO}
              </Box>
            }
          />
        </StackColumn>
        {progressVisible && (
          <TopAbsolute>
            <LinearProgress color="secondary" />
          </TopAbsolute>
        )}
        {(saveStep === 'zipping' ||
          saveStep === 'uploading' ||
          saveStep === 'saving' ||
          saveStep === 'success') && (
          <Center
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: '#0008',
              color: 'pink',
            }}
          >
            {saveStep === 'zipping' && <Typography>Compressing...</Typography>}
            {saveStep === 'uploading' && <Typography>UPLOADING...</Typography>}
            {saveStep === 'saving' && <Typography>Saving...</Typography>}
            {saveStep === 'success' && <Typography>Saved!!</Typography>}
          </Center>
        )}
      </DialogContent>
      <DialogActions>
        {(saveStep === 'none' || saveStep === 'error') && (
          <Button onClick={handleClickSaveBtn} color="secondary">
            저장
          </Button>
        )}
        <Button
          onClick={handleClose}
          disabled={saveStep !== 'none' && saveStep !== 'error' && saveStep !== 'success'}
        >
          닫기
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}
